/**
 * Connect email folder/thread UI cache on TotalAccessOfflineDB (replaces legacy `totalaccess_email_local_cache` raw IndexedDB).
 *
 * Outbox/drafts remain in `emailsStore` (`outbox_emails` / `email_drafts`). This module is list-view cache only.
 */
import Dexie from 'dexie';

import offlinePayloadCrypto from './offlineCrypto';
import type { OfflineDomainRecord } from './offlineDb';
import offlineDb from './offlineDb';

export const EMAIL_UI_LEGACY_DB_NAME = 'totalaccess_email_local_cache';

const DOMAIN = 'emails' as const;
export const EMAIL_UI_STORE_THREADS = 'connect_cache_threads' as const;
export const EMAIL_UI_STORE_EMAILS = 'connect_cache_emails' as const;
export const EMAIL_UI_STORE_FOLDERS = 'connect_cache_folders' as const;
export const EMAIL_UI_STORE_SYNC = 'connect_cache_sync' as const;
export const EMAIL_UI_STORE_THREAD_DETAILS = 'connect_cache_thread_details' as const;

const STORE_THREADS = EMAIL_UI_STORE_THREADS;
const STORE_EMAILS = EMAIL_UI_STORE_EMAILS;
const STORE_FOLDERS = EMAIL_UI_STORE_FOLDERS;
const STORE_SYNC = EMAIL_UI_STORE_SYNC;
const STORE_THREAD_DETAILS = EMAIL_UI_STORE_THREAD_DETAILS;

const LEGACY_MIGRATE_GUARD = '__ta_email_ui_cache_migrated_to_unified_db_v2__';

export type EmailUiStores =
  | typeof STORE_THREADS
  | typeof STORE_EMAILS
  | typeof STORE_FOLDERS
  | typeof STORE_SYNC
  | typeof STORE_THREAD_DETAILS;

export interface CachedThreadPayload extends Record<string, unknown> {
  thread_id?: string;
  last_message_at?: string;
  _folder?: string;
  _accountId?: number;
  _cachedAt?: number;
}

export interface SyncMetaPayload extends Record<string, unknown> {
  folder?: string;
  accountId?: number;
  lastSyncAt?: number;
  lastUid?: number;
  messageCount?: number;
  unreadCount?: number;
}

function getDb() {
  return offlineDb;
}

export function getPortalClientId(): number {
  if (typeof window === 'undefined') {
    return 0;
  }
  const raw = window.localStorage.getItem('current_client_id');
  const n = raw ? parseInt(raw, 10) : 0;
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function threadEntityId(threadId: string): string {
  try {
    const b = btoa(unescape(encodeURIComponent(threadId)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
    return `tid:${b}`;
  } catch {
    return `tid:${encodeURIComponent(threadId).slice(0, 384)}`;
  }
}

function folderEntity(path: string, accountId: number): string {
  return `f:${accountId}:${encodeURIComponent(path)}`;
}

function syncEntityId(folderKey: string, accountId: number): string {
  return `syn:${encodeURIComponent(folderKey)}:${accountId}`;
}

function emailEntityId(messageKey: string): string {
  return `em:${encodeURIComponent(messageKey).slice(0, 400)}`;
}

function threadDetailEntityId(threadId: string, accountId: number): string {
  return `td:${accountId}:${threadEntityId(threadId)}`;
}

async function decryptRecordPayload<T extends Record<string, unknown>>(
  record: OfflineDomainRecord | undefined,
): Promise<T | undefined> {
  if (!record) {
    return undefined;
  }
  return offlinePayloadCrypto.decryptValue<T>(record.payload);
}

async function buildRecordRow<T extends Record<string, unknown>>(
  store: EmailUiStores,
  clientId: number,
  entityId: string,
  payload: T,
  existing?: OfflineDomainRecord | undefined,
): Promise<OfflineDomainRecord> {
  const now = new Date().toISOString();
  const enc = await offlinePayloadCrypto.encryptValue(payload);
  return {
    id: existing?.id,
    domain: DOMAIN,
    store,
    client_id: clientId,
    entity_id: entityId,
    sync_status: 'synced',
    created_at: existing?.created_at || now,
    updated_at: now,
    payload: enc,
    payload_size: enc.length,
  };
}

async function upsertOpaque(
  store: EmailUiStores,
  clientId: number,
  entityId: string,
  payload: Record<string, unknown>,
): Promise<void> {
  const db = getDb();
  if (!db || !clientId) {
    return;
  }

  const existing = await db.records
    .where('[domain+store+client_id+entity_id]')
    .equals([DOMAIN, store, clientId, entityId])
    .first();
  await db.records.put(await buildRecordRow(store, clientId, entityId, payload, existing));
}

export const emailUiCacheStore = {
  async getEncryptionSetting(clientId: number): Promise<{ enabled: boolean } | null> {
    const raw = await this.getOpaqueMeta<{ enabled?: boolean }>(clientId, 'enc');
    return raw?.enabled !== undefined ? { enabled: Boolean(raw.enabled) } : null;
  },

  async setEncryptionSetting(clientId: number, enabled: boolean): Promise<void> {
    await this.setOpaqueMeta(clientId, 'enc', { enabled });
  },

  /** Small meta blobs keyed by segment (stored on `offlineDb.meta`). */
  async getOpaqueMeta<T>(clientId: number, segment: string): Promise<T | undefined> {
    const db = getDb();
    if (!db || !clientId) {
      return undefined;
    }
    const key = `${DOMAIN}:${clientId}:email_ui:${segment}`;
    const row = await db.meta.get(key);
    if (!row?.payload) {
      return undefined;
    }
    return offlinePayloadCrypto.decryptValue<T>(row.payload);
  },

  async setOpaqueMeta(clientId: number, segment: string, value: Record<string, unknown>): Promise<void> {
    const db = getDb();
    if (!db || !clientId) {
      return;
    }
    const key = `${DOMAIN}:${clientId}:email_ui:${segment}`;
    const rowPayload = await offlinePayloadCrypto.encryptValue(value);
    await db.meta.put({
      key,
      domain: DOMAIN,
      client_id: clientId,
      updated_at: new Date().toISOString(),
      payload: rowPayload,
    });
  },

  async deleteOpaqueMeta(clientId: number, segment: string): Promise<void> {
    const db = getDb();
    if (!db || !clientId) {
      return;
    }
    const key = `${DOMAIN}:${clientId}:email_ui:${segment}`;
    await db.meta.delete(key).catch(() => undefined);
  },

  async storeThreads(
    clientId: number,
    threads: CachedThreadPayload[],
    folder: string,
    accountId: number,
  ): Promise<void> {
    for (const t of threads) {
      const threadId = String(t.thread_id || '');
      if (!threadId) {
        continue;
      }
      const payload: CachedThreadPayload = {
        ...t,
        _cachedAt: Date.now(),
        _accountId: accountId,
        _folder: folder,
      };
      await upsertOpaque(STORE_THREADS, clientId, threadEntityId(threadId), payload);
    }
  },

  async getThreads(
    clientId: number,
    folder: string,
    accountId: number,
    options?: { limit?: number; offset?: number },
  ): Promise<CachedThreadPayload[]> {
    const db = getDb();
    if (!db || !clientId) {
      return [];
    }
    const rows = await db.records.where('[domain+store+client_id]').equals([DOMAIN, STORE_THREADS, clientId]).toArray();
    const payloads: CachedThreadPayload[] = [];
    for (const row of rows) {
      const v = await decryptRecordPayload<CachedThreadPayload>(row);
      if (v && typeof v === 'object' && v._folder === folder && v._accountId === accountId) {
        payloads.push(v);
      }
    }
    const limit = options?.limit ?? 0;
    const offset = options?.offset ?? 0;

    payloads.sort((a, b) => {
      const dateA = new Date(String(a.last_message_at || a._cachedAt || 0)).getTime();
      const dateB = new Date(String(b.last_message_at || b._cachedAt || 0)).getTime();
      return dateB - dateA;
    });

    if (limit > 0) {
      return payloads.slice(offset, offset + limit);
    }
    if (offset > 0) {
      return payloads.slice(offset);
    }
    return payloads;
  },

  async hasCachedThreads(clientId: number, folder: string, accountId: number): Promise<boolean> {
    const threads = await this.getThreads(clientId, folder, accountId, { limit: 1 });
    return threads.length > 0;
  },

  async clearThreads(clientId: number, folder?: string, accountId?: number): Promise<void> {
    const db = getDb();
    if (!db || !clientId) {
      return;
    }
    if (folder !== undefined && accountId !== undefined) {
      const rows = await db.records.where('[domain+store+client_id]').equals([DOMAIN, STORE_THREADS, clientId]).toArray();
      const ids: number[] = [];
      for (const row of rows) {
        const v = await decryptRecordPayload<CachedThreadPayload>(row);
        if (v && typeof v === 'object' && v._folder === folder && v._accountId === accountId && typeof row.id === 'number') {
          ids.push(row.id);
        }
      }
      if (ids.length > 0) {
        await db.records.bulkDelete(ids);
      }
      return;
    }
    await this.deleteAllInStore(clientId, STORE_THREADS);
  },

  async moveThreadToFolder(
    clientId: number,
    threadId: string,
    toFolder: string,
    accountId: number,
    updates?: {
      last_message_folder?: string;
      last_sent_at?: string;
      last_sent_preview?: string;
      last_sent_from?: { name?: string; address: string };
    },
  ): Promise<void> {
    const entity = threadEntityId(threadId);
    const db = getDb();
    if (!db || !clientId) {
      return;
    }
    const row = await db.records
      .where('[domain+store+client_id+entity_id]')
      .equals([DOMAIN, STORE_THREADS, clientId, entity])
      .first();
    const payload = await decryptRecordPayload<CachedThreadPayload>(row);
    if (!payload || typeof payload !== 'object') {
      return;
    }
    if ((payload._accountId as number) !== accountId) {
      return;
    }
    const next: CachedThreadPayload = {
      ...payload,
      ...updates,
      _folder: toFolder,
      _cachedAt: Date.now(),
    };
    await upsertOpaque(STORE_THREADS, clientId, entity, next);
  },

  async deleteThread(clientId: number, threadId: string, accountId?: number): Promise<void> {
    const entity = threadEntityId(threadId);
    const db = getDb();
    if (!db || !clientId) {
      return;
    }
    const row = await db.records
      .where('[domain+store+client_id+entity_id]')
      .equals([DOMAIN, STORE_THREADS, clientId, entity])
      .first();

    const payload = await decryptRecordPayload<CachedThreadPayload>(row);
    if (!payload || typeof payload !== 'object') {
      return;
    }

    if (accountId !== undefined && (payload._accountId as number) !== accountId) {
      return;
    }

    const idNum = typeof row?.id === 'number' ? row!.id : undefined;
    if (idNum !== undefined) {
      await db.records.delete(idNum);
    }

    await this.deleteThreadDetail(clientId, threadId, accountId);
  },

  async storeThreadDetail(
    clientId: number,
    threadId: string,
    accountId: number,
    thread: Record<string, unknown>,
  ): Promise<void> {
    if (!threadId) {
      return;
    }
    await upsertOpaque(STORE_THREAD_DETAILS, clientId, threadDetailEntityId(threadId, accountId), {
      ...thread,
      _accountId: accountId,
      _cachedAt: Date.now(),
    });
  },

  async getThreadDetail<T extends Record<string, unknown>>(
    clientId: number,
    threadId: string,
    accountId: number,
  ): Promise<T | null> {
    const db = getDb();
    if (!db || !clientId || !threadId) {
      return null;
    }
    const row = await db.records
      .where('[domain+store+client_id+entity_id]')
      .equals([DOMAIN, STORE_THREAD_DETAILS, clientId, threadDetailEntityId(threadId, accountId)])
      .first();
    const payload = await decryptRecordPayload<T>(row);
    return payload && typeof payload === 'object' ? payload : null;
  },

  async deleteThreadDetail(clientId: number, threadId: string, accountId?: number): Promise<void> {
    const db = getDb();
    if (!db || !clientId || !threadId) {
      return;
    }

    if (accountId !== undefined) {
      const row = await db.records
        .where('[domain+store+client_id+entity_id]')
        .equals([DOMAIN, STORE_THREAD_DETAILS, clientId, threadDetailEntityId(threadId, accountId)])
        .first();
      if (typeof row?.id === 'number') {
        await db.records.delete(row.id);
      }
      return;
    }

    const rows = await db.records.where('[domain+store+client_id]').equals([DOMAIN, STORE_THREAD_DETAILS, clientId]).toArray();
    const ids: number[] = [];
    for (const row of rows) {
      const v = await decryptRecordPayload<{ thread_id?: string }>(row);
      if (v?.thread_id === threadId && typeof row.id === 'number') {
        ids.push(row.id);
      }
    }
    if (ids.length > 0) {
      await db.records.bulkDelete(ids);
    }
  },

  async updateSyncMeta(
    clientId: number,
    folderCompoundKey: string,
    accountId: number,
    metadata: Partial<SyncMetaPayload>,
  ): Promise<void> {
    if (!clientId) {
      return;
    }
    const eid = syncEntityId(folderCompoundKey, accountId);
    const db = getDb();
    if (!db) {
      return;
    }

    const row = await db.records
      .where('[domain+store+client_id+entity_id]')
      .equals([DOMAIN, STORE_SYNC, clientId, eid])
      .first();
    const cur = row ? await decryptRecordPayload<SyncMetaPayload>(row) : undefined;
    const next: SyncMetaPayload = {
      ...(cur ?? {}),
      folder: folderCompoundKey,
      accountId,
      ...(metadata ?? {}),
      lastSyncAt: Date.now(),
    };
    await upsertOpaque(STORE_SYNC, clientId, eid, next as Record<string, unknown>);
  },

  async getSyncMeta(clientId: number, folderCompoundKey: string, accountId: number): Promise<SyncMetaPayload | null> {
    const db = getDb();
    if (!db || !clientId) {
      return null;
    }
    const eid = syncEntityId(folderCompoundKey, accountId);
    const row = await db.records
      .where('[domain+store+client_id+entity_id]')
      .equals([DOMAIN, STORE_SYNC, clientId, eid])
      .first();

    const v = await decryptRecordPayload<SyncMetaPayload>(row);
    return v && typeof v === 'object' ? v : null;
  },

  async storeFolders(
    clientId: number,
    folders: Array<Record<string, unknown>>,
    accountId: number,
  ): Promise<void> {
    await this.deleteAllFoldersForAccount(clientId, accountId);
    for (const folder of folders) {
      const path = String(folder.path ?? '');
      if (!path) {
        continue;
      }
      await upsertOpaque(STORE_FOLDERS, clientId, folderEntity(path, accountId), {
        ...folder,
        accountId,
      });
    }
  },

  async deleteAllFoldersForAccount(clientId: number, accountId: number): Promise<void> {
    const db = getDb();
    if (!db || !clientId) {
      return;
    }
    const rows = await db.records.where('[domain+store+client_id]').equals([DOMAIN, STORE_FOLDERS, clientId]).toArray();

    const ids: number[] = [];
    for (const row of rows) {
      const v = await decryptRecordPayload<{ accountId?: number }>(row);
      if (v?.accountId === accountId && typeof row.id === 'number') {
        ids.push(row.id);
      }
    }
    if (ids.length > 0) {
      await db.records.bulkDelete(ids);
    }
  },

  async getFolders(clientId: number, accountId: number): Promise<Record<string, unknown>[]> {
    const db = getDb();
    if (!db || !clientId) {
      return [];
    }

    const rows = await db.records.where('[domain+store+client_id]').equals([DOMAIN, STORE_FOLDERS, clientId]).toArray();
    const values: Record<string, unknown>[] = [];

    for (const row of rows) {
      const v = await decryptRecordPayload<Record<string, unknown>>(row);
      if (v?.accountId === accountId) {
        values.push(v);
      }
    }
    return values;
  },

  async countRows(clientId: number, store: EmailUiStores): Promise<number> {
    const db = getDb();
    if (!db || !clientId) {
      return 0;
    }
    return db.records.where('[domain+store+client_id]').equals([DOMAIN, store, clientId]).count();
  },

  /** Thread + email payloads for pruning (decrypted). */
  async listAllThreadsForPrune(clientId: number): Promise<CachedThreadPayload[]> {
    const db = getDb();
    if (!db || !clientId) {
      return [];
    }
    const rows = await db.records.where('[domain+store+client_id]').equals([DOMAIN, STORE_THREADS, clientId]).toArray();

    const out: CachedThreadPayload[] = [];

    for (const row of rows) {
      const v = await decryptRecordPayload<CachedThreadPayload>(row);
      if (v && typeof v === 'object') {
        out.push(v);
      }
    }
    return out;
  },

  async deleteThreadRowByNumericId(threadRowId: number): Promise<void> {
    const db = getDb();
    if (!db) {
      return;
    }

    await db.records.delete(threadRowId);
  },

  async listAllEmailsForPrune(clientId: number): Promise<Array<{ _cache?: number } & Record<string, unknown>>> {
    const db = getDb();
    if (!db || !clientId) {
      return [];
    }
    const rows = await db.records.where('[domain+store+client_id]').equals([DOMAIN, STORE_EMAILS, clientId]).toArray();
    const out: Array<{ _cache?: number } & Record<string, unknown>> = [];
    for (const row of rows) {
      const v = await decryptRecordPayload(row);
      if (v && typeof v === 'object') {
        out.push({ ...(v as Record<string, unknown>), _cache: row.id });
      }
    }
    return out;
  },

  async deleteEmailByRowId(emailRowId: number): Promise<void> {
    const db = getDb();
    if (!db) {
      return;
    }

    await db.records.delete(emailRowId);
  },

  async deleteAllInStore(clientId: number, store: EmailUiStores): Promise<void> {
    const db = getDb();
    if (!db || !clientId) {
      return;
    }
    const rows = await db.records.where('[domain+store+client_id]').equals([DOMAIN, store, clientId]).toArray();

    const ids = rows.map((r) => r.id).filter((id): id is number => typeof id === 'number');
    if (ids.length > 0) {
      await db.records.bulkDelete(ids);
    }
  },

  /** Clears cached folders/threads/email rows + sync blobs; keeps encryption prefs (parity with legacy `clearAllCache`). */
  async clearEverythingForTenant(clientId: number): Promise<void> {
    if (!clientId) {
      return;
    }
    await Promise.all([
      this.deleteAllInStore(clientId, STORE_THREADS),
      this.deleteAllInStore(clientId, STORE_EMAILS),
      this.deleteAllInStore(clientId, STORE_FOLDERS),
      this.deleteAllInStore(clientId, STORE_SYNC),
      this.deleteAllInStore(clientId, STORE_THREAD_DETAILS),
    ]);
  },
};

async function deleteAllLegacyEmailUiCaches(): Promise<void> {
  try {
    indexedDB.deleteDatabase(EMAIL_UI_LEGACY_DB_NAME);
  } catch {
    /* ignore */
  }
  await Dexie.delete(EMAIL_UI_LEGACY_DB_NAME).catch(() => undefined);
}

function readWholeStore(db: IDBDatabase, storeName: string): Promise<unknown[]> {
  return new Promise((resolve, reject) => {
    try {
      const txn = db.transaction(storeName, 'readonly');
      const req = txn.objectStore(storeName).getAll();
      req.onsuccess = () => resolve(Array.isArray(req.result) ? [...req.result] : []);
      req.onerror = () => reject(req.error ?? new Error('IDB getAll failed'));
    } catch (e) {
      reject(e instanceof Error ? e : new Error(String(e)));
    }
  });
}

/** Read legacy `encryption_config` flag before migrating (session AES key hydrates afterward). */
export async function readLegacyEncryptionEnvelope(): Promise<boolean | null> {
  if (typeof indexedDB === 'undefined') {
    return null;
  }
  const exists = await Dexie.exists(EMAIL_UI_LEGACY_DB_NAME);
  if (!exists) {
    return null;
  }
  try {
    return await new Promise<boolean | null>((resolve) => {
      const req = indexedDB.open(EMAIL_UI_LEGACY_DB_NAME);
      req.onerror = () => resolve(null);

      req.onsuccess = () => {
        const legacy = req.result;

        try {
          if (!legacy.objectStoreNames.contains('encryption_config')) {
            legacy.close?.();
            resolve(null);
            return;
          }

          const g = legacy.transaction('encryption_config', 'readonly').objectStore('encryption_config').get('config');

          g.onerror = () => {
            legacy.close?.();

            resolve(null);
          };

          g.onsuccess = () => {
            const cfgVal = g.result;

            legacy.close?.();
            resolve(
              typeof cfgVal === 'object' && cfgVal !== null && typeof (cfgVal as { enabled?: boolean }).enabled === 'boolean'
                ? !!(cfgVal as { enabled?: boolean }).enabled
                : null,
            );
          };
        } catch {
          legacy.close?.();

          resolve(null);
        }
      };
    });
  } catch {
    return null;
  }
}

async function migrateEncryptionPrefsOnly(clientId: number, legacyDb: IDBDatabase): Promise<void> {
  if (!legacyDb.objectStoreNames.contains('encryption_config')) {
    return;
  }
  return new Promise((resolve) => {
    try {
      const txn = legacyDb.transaction('encryption_config', 'readonly');
      const g = txn.objectStore('encryption_config').get('config');
      g.onsuccess = () => {
        const cfgVal = g.result;

        void emailUiCacheStore
          .setOpaqueMeta(clientId, 'enc', {
            enabled: Boolean((cfgVal as { enabled?: boolean } | null | undefined)?.enabled),
          })
          .then(() => resolve())
          .catch(() => resolve());
      };
      g.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}

/**
 * Import rows from legacy `totalaccess_email_local_cache` → TotalAccessOfflineDB.
 * Prefer calling from `EmailCacheService` after AES session decrypt is wired so `{ iv, data }` rows unwrap.
 *
 * When `normalizeRow` is omitted, records are copied verbatim (plaintext legacy rows still migrate).
 */
export async function migrateLegacyEmailUICacheDatabaseIfNeeded(normalizeRow?: (
  raw: Record<string, unknown>,
) => Promise<Record<string, unknown>>): Promise<void> {
  if (typeof window === 'undefined' || typeof indexedDB === 'undefined' || !offlineDb) {
    return;
  }

  if (window.localStorage.getItem(LEGACY_MIGRATE_GUARD) === '1') {
    return;
  }

  const existsLegacy = await Dexie.exists(EMAIL_UI_LEGACY_DB_NAME);
  if (!existsLegacy) {
    window.localStorage.setItem(LEGACY_MIGRATE_GUARD, '1');
    return;
  }

  const clientId = getPortalClientId();
  /** Need tenant isolation on TotalAccessOfflineDB; retry after login / client resolved. */
  if (!clientId) {
    return;
  }

  let db: IDBDatabase | undefined;

  const unwrap = normalizeRow ?? (async (r: Record<string, unknown>) => r);

  try {
    db = await new Promise<IDBDatabase>((resolve, reject) => {
      const req = indexedDB.open(EMAIL_UI_LEGACY_DB_NAME);

      req.onsuccess = () => resolve(req.result);

      req.onblocked = () =>
        reject(new Error('[emailUiCacheStore] IndexedDB migrate open blocked'));

      req.onerror = () =>
        reject(req.error ?? new Error('[emailUiCacheStore] IndexedDB migrate open failed'));

      req.onupgradeneeded = () => {};
    });

    if (db.objectStoreNames.contains('threads')) {
      try {
        const rows = await readWholeStore(db, 'threads');
        const arr = rows as CachedThreadPayload[];
        for (const thr of arr) {
          const flattened = await unwrap(thr as unknown as Record<string, unknown>);
          const tid = String(flattened.thread_id || '');
          if (!tid) continue;

          await upsertOpaque(
            STORE_THREADS,
            clientId,
            threadEntityId(tid),
            flattened as Record<string, unknown>,
          );
        }
      } catch (e) {
        console.warn('[emailUiCacheStore] Migrate threads skipped:', e);
      }
    }

    if (db.objectStoreNames.contains('emails')) {
      try {
        const emails = await readWholeStore(db, 'emails');
        for (const row of emails) {
          const raw = row as Record<string, unknown>;
          const e = await unwrap(raw);
          const mk = String((e.messageId ?? e.message_id ?? '') as string);
          const keySuffix =
            mk || String(typeof e['_cacheId'] === 'number' ? e['_cacheId'] : Date.now() + Math.random());
          await upsertOpaque(
            STORE_EMAILS,
            clientId,
            emailEntityId(keySuffix.slice(0, 450)),

            e,
          );
        }
      } catch (e) {
        console.warn('[emailUiCacheStore] Migrate emails skipped:', e);
      }
    }

    if (db.objectStoreNames.contains('folders')) {
      try {
        const folders = (await readWholeStore(db, 'folders')) as Array<Record<string, unknown>>;
        for (const folder of folders) {
          const path = String(folder.path ?? '');
          const aid = Number(folder.accountId ?? 0);
          if (!path || !aid) continue;

          const f = await unwrap(folder);

          await upsertOpaque(STORE_FOLDERS, clientId, folderEntity(path, aid), f);
        }
      } catch (e) {
        console.warn('[emailUiCacheStore] Migrate folders skipped:', e);
      }
    }

    if (db.objectStoreNames.contains('sync_metadata')) {
      try {
        const metas = await readWholeStore(db, 'sync_metadata');
        for (const row of metas) {
          const v = await unwrap(row as Record<string, unknown>);
          const folderLabel = String(v.folder ?? '');
          const aid = Number(v.accountId ?? 0);
          if (!folderLabel || !aid) continue;

          const eid = syncEntityId(folderLabel, aid);
          await upsertOpaque(STORE_SYNC, clientId, eid, v);
        }
      } catch (e) {
        console.warn('[emailUiCacheStore] Migrate sync skipped:', e);
      }
    }

    await migrateEncryptionPrefsOnly(clientId, db);

    window.localStorage.setItem(LEGACY_MIGRATE_GUARD, '1');

    db.close?.();
    db = undefined;

    await deleteAllLegacyEmailUiCaches();
  } catch (err) {
    console.warn('[emailUiCacheStore] Legacy migration deferred or failed:', err);
  } finally {
    db?.close();
  }
}

export default emailUiCacheStore;
